import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  DraftsResponse,
  HistoryResponse,
  IncidentsFile,
  LeagueBundle,
  LineupsFile,
  MatchupsResponse,
  PlayersFile,
  ScheduleResponse,
  ScoresResponse,
  TransactionsResponse,
} from '../../shared/types';

async function getJson<T>(url: string, signal: AbortSignal): Promise<T> {
  const res = await fetch(url, { signal });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) detail = body.message;
    } catch {
      /* response was not JSON; the status line is the best we have */
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Minimal fetch-on-mount hook with abort + manual reload.
 *
 * Deliberately not a caching library: this site has three endpoints and the
 * Worker already caches at the edge, so an in-memory promise map (below) is
 * enough to stop duplicate requests when two views mount at once.
 */
function useResource<T>(url: string, loader: (signal: AbortSignal) => Promise<T>): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    const ctrl = new AbortController();
    let alive = true;
    setLoading(true);
    setError(null);

    loaderRef
      .current(ctrl.signal)
      .then((d) => {
        if (alive) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!alive || ctrl.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Request failed');
        setLoading(false);
      });

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [url, nonce]);

  const reload = useCallback(() => {
    // Clear everything rather than just `url`: some resources are composed from
    // several requests under a synthetic key (see useScores), so deleting the
    // key alone would replay the same cached failures.
    inflight.clear();
    setNonce((n) => n + 1);
  }, []);

  return { data, error, loading, reload };
}

/**
 * Dedupes concurrent requests for the same URL across components. Without this,
 * mounting the header and a view that both want /api/league fires two requests.
 */
const inflight = new Map<string, Promise<unknown>>();

function shared<T>(url: string, signal: AbortSignal): Promise<T> {
  const existing = inflight.get(url) as Promise<T> | undefined;
  if (existing) return existing;

  // Use a detached signal for the shared promise so one component unmounting
  // does not abort the request another component is still waiting on.
  const p = getJson<T>(url, new AbortController().signal).catch((err) => {
    inflight.delete(url);
    throw err;
  });
  inflight.set(url, p);

  return new Promise<T>((resolve, reject) => {
    signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), {
      once: true,
    });
    p.then(resolve, reject);
  });
}

export const useLeague = () =>
  useResource<LeagueBundle>('/api/league', (s) => shared<LeagueBundle>('/api/league', s));

export const useHistory = () =>
  useResource<HistoryResponse>('/api/history', (s) => shared<HistoryResponse>('/api/history', s));

export const useTransactions = () =>
  useResource<TransactionsResponse>('/api/transactions', (s) =>
    shared<TransactionsResponse>('/api/transactions', s),
  );

export const usePlayers = () =>
  useResource<PlayersFile>('/data/players.json', (s) => shared<PlayersFile>('/data/players.json', s));

/**
 * The build-time lineup ledger backing the coaching and luck pages.
 *
 * A static asset, not an /api route, and deliberately so: it needs one Sleeper
 * call per week per season plus a 2 MB projections download each time, which no
 * Worker invocation could afford. Being static also means the edge serves it
 * without invoking the Worker at all.
 */
export const useLineups = () =>
  useResource<LineupsFile>('/data/lineups.json', (s) => shared<LineupsFile>('/data/lineups.json', s));

/**
 * The append-only off-field incident ledger.
 *
 * Static like the two above, and cheap for the same reason the CLAUDE.md notes
 * give: a request that matches a file never invokes the Worker, so this does
 * not spend any of the free tier's budget. It is also the smallest file the
 * site fetches -- one row per player-season -- because it records only players
 * a manager in THIS league actually held.
 */
export const useIncidents = () =>
  useResource<IncidentsFile>('/data/incidents.json', (s) =>
    shared<IncidentsFile>('/data/incidents.json', s),
  );

export const useDrafts = () =>
  useResource<DraftsResponse>('/api/drafts', (s) => shared<DraftsResponse>('/api/drafts', s));

export function useMatchups(leagueId: string, week: number) {
  const url = `/api/matchups?league=${leagueId}&week=${week}`;
  return useResource<MatchupsResponse>(url, (s) => shared<MatchupsResponse>(url, s));
}

/**
 * The regular-season fixture list, including weeks not yet played.
 *
 * Separate from `useScores` on purpose -- that hook's payload stops at the
 * games that have happened, which is exactly what a schedule cannot use.
 */
export function useSchedule(leagueId: string) {
  const url = `/api/schedule?league=${leagueId}`;
  return useResource<ScheduleResponse>(url, (s) => shared<ScheduleResponse>(url, s));
}

/**
 * Season scores for several leagues at once, one request each.
 *
 * Head-to-head records span every season the league has played, but the Worker
 * deliberately serves one season per call to stay inside the 50-subrequest
 * limit. Fanning out here means each season gets its own budget, so this keeps
 * working no matter how many seasons accumulate.
 */
export function useScores(leagueIds: string[]) {
  const key = leagueIds.join(',');
  return useResource<ScoresResponse[]>(`scores:${key}`, async (signal) => {
    if (leagueIds.length === 0) return [];
    return Promise.all(
      leagueIds.map((id) => shared<ScoresResponse>(`/api/scores?league=${id}`, signal)),
    );
  });
}

/**
 * Transactions for several league years at once, one request each.
 *
 * Same reasoning as `useScores`: the Worker serves one season per call to stay
 * inside its subrequest budget, so following a pick through the seasons it was
 * traded in means fanning out here rather than there.
 */
export function useAllTransactions(leagueIds: string[]) {
  const key = leagueIds.join(',');
  return useResource<TransactionsResponse[]>(`transactions:${key}`, async (signal) => {
    if (leagueIds.length === 0) return [];
    return Promise.all(
      leagueIds.map((id) => shared<TransactionsResponse>(`/api/transactions?league=${id}`, signal)),
    );
  });
}
